import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormik } from "formik";
import { useOpsContext } from "../../../ContextsAndProvider/OpsContext";
import { searchPatient } from "../../apps/api/Patient";
import { failed } from "../../common/Toastify";
import TextInput from "../../common/textfield/TextInput";
import { DateSelectorWithoutFormik } from "../../common/textfield/DateSelector";
import moment from "moment";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { Tooltip } from "@mui/material";
import CustomTable from "../../common/table/CustomTable";
import {
  dashboardPatientsAssignedHeadCells,
  dashboardPatientsAssignedDataFields,
} from "../../apps/Patient/Constants";
import { patientSelectionFormikObject } from "../Dashboard/Constant";
import "./OpsDashboard.css";

const OpsPatientsTab = () => {
  const { selectedOrganization, selectedPractitioner } = useOpsContext();
  const [searchData, setSearchData] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [page, setPage] = useState(0);

  const handleDateChange = (val) => {
    if (val == null || val === "") {
      formik.setFieldValue("birthDate", "");
    } else {
      const momentDate = moment(val, "MMM-DD-YYYY", true);
      if (momentDate.isValid()) {
        formik.setFieldValue("birthDate", moment(val).format("MM-DD-YYYY")?.replace(/-/g, "/"));
      } else {
        formik.setFieldValue("birthDate", val);
        formik.setFieldError("birthDate", "Invalid date format");
      }
    }
  };

  const handleClearDate = () => {
    formik.setFieldValue("birthDate", "");
    formik.setFieldError("birthDate", undefined);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      birthDate: "",
      healthCardNumber: "",
    },
    ...patientSelectionFormikObject,
    onSubmit: async (values) => {
      values.firstName = values.firstName?.trim();
      values.lastName = values.lastName?.trim();
      values.birthDate = values.birthDate?.trim();
      values.healthCardNumber = values.healthCardNumber?.trim();
      if (
        !values.firstName &&
        !values.lastName &&
        !values.birthDate &&
        !values.healthCardNumber
      ) {
        formik.setFieldError(
          "allSearchFieldsEmpty",
          "There must be at least one search criteria other than organization"
        );
        setSearchData([]);
        return;
      }
      formik.setFieldError("allSearchFieldsEmpty", undefined);
      setBtnLoading(true);
      try {
        const res = await searchPatient({ ...values, org_id: selectedOrganization });
        if (res.status === true) {
          if (res.errors && Array.isArray(res.errors) && res.errors.length > 0) {
            res.errors.forEach((error) => {
              const msg =
                error.source && error.message
                  ? `${error.source === "ecr" ? "Client Registry" : error.source}: ${error.message}`
                  : error.message || error.error || "An error occurred";
              failed(msg);
            });
          }
          setSearchData(res.result || []);
        } else {
          failed(res.error);
          setSearchData([]);
        }
      } catch (err) {
        failed(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err.message
        );
        setSearchData([]);
      } finally {
        setBtnLoading(false);
      }
    },
  });

  const handleSearchPatient = (e) => {
    e.preventDefault();
    setPage(0);
    if (!btnLoading) formik.handleSubmit();
  };

  const handlePatientClick = (data) => {
    const patientId = data?.id;
    if (!patientId) {
      failed("Patient is sourced externally and does not exist in Florizel yet.");
      return;
    }
    const url = `${window.location.origin}/app/patients-details?patientId=${patientId}`;
    window.open(url, "_blank");
  };

  const selectedUserAction = ({ action, data }) => {
    if (action === "view") handlePatientClick(data);
  };

  if (!selectedOrganization || !selectedPractitioner) return null;

  return (
    <div className="ops-dashboard-content">
      <div style={{ display: "flex", justifyContent: "flex-start" }} className="dashSearch">
        <Row>
          <Col>
            <TextInput
              keyField="firstName"
              label="First Name"
              formik={formik}
              placeholder="First Name"
              hideRequired={true}
            />
          </Col>
          <Col>
            <TextInput
              keyField="lastName"
              label="Last Name"
              formik={formik}
              placeholder="Last Name"
              hideRequired={true}
            />
          </Col>
          <Col>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ flex: 1 }}>
                <DateSelectorWithoutFormik
                  formik={formik}
                  keyField="birthDate"
                  label="DOB"
                  value={
                    formik.values.birthDate
                      ? moment(formik.values.birthDate).format("YYYY-MM-DD")
                      : null
                  }
                  handleChange={handleDateChange}
                  maxDate={moment()}
                  hideRequired={true}
                />
              </div>
              {formik.values.birthDate && (
                <Tooltip title="Clear">
                  <ClearIcon
                    style={{ color: "#666", cursor: "pointer", fontSize: "20px", alignSelf: "center" }}
                    onClick={handleClearDate}
                  />
                </Tooltip>
              )}
            </div>
          </Col>
          <Col>
            <TextInput
              keyField="healthCardNumber"
              label="Patient Identifier"
              formik={formik}
              placeholder="Patient Identifier"
              hideRequired={true}
              tooltip="Searches for exact Patient Identifier match"
            />
          </Col>
          <Col>
            <button
              style={{ height: "60px", marginTop: "26px", width: "fit-content" }}
              className="custom-btn"
              type="button"
              disabled={btnLoading}
              onClick={handleSearchPatient}
            >
              <SearchOutlinedIcon /> Search
            </button>
            {formik.errors.allSearchFieldsEmpty && (
              <div className="error-text">{formik.errors.allSearchFieldsEmpty}</div>
            )}
          </Col>
        </Row>
      </div>
      <CustomTable
        tableData={searchData}
        headCells={dashboardPatientsAssignedHeadCells}
        dataFields={dashboardPatientsAssignedDataFields}
        selectedUserAction={selectedUserAction}
        rowOptions={false}
        rowClick={true}
        loading={btnLoading}
        fetching={false}
        deletingItem={false}
        canView={true}
        canDelete={false}
        rowsPerPage={5}
        canEdit={false}
        page={page}
        setPage={setPage}
      />
    </div>
  );
};

export default OpsPatientsTab;
